import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getToken } from '../../api';
import SwipeTest from '../../components/SwipeTest';
import { femaleBodyPhotos, maleBodyPhotos, mixedBodyPhotos } from '../../data/swipePhotos';

function scoreSwipe(photos, results) {
  const liked = photos.filter((_, i) => results[i] === 'yes');
  const counts = {
    vata:  liked.filter(p => p.type.toUpperCase().includes('V') || p.type === 'vata').length,
    pitta: liked.filter(p => p.type.toUpperCase().includes('P') || p.type === 'pitta').length,
    kapha: liked.filter(p => p.type.toUpperCase().includes('K') || p.type === 'kapha').length,
  };
  const total = Math.max(counts.vata + counts.pitta + counts.kapha, 1);
  return {
    vata:  Math.round((counts.vata  / total) * 100),
    pitta: Math.round((counts.pitta / total) * 100),
    kapha: Math.round((counts.kapha / total) * 100),
    dominant: Object.keys(counts).reduce((a, b) => counts[a] > counts[b] ? a : b),
  };
}

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

export default function SwipeBodies() {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);

  useEffect(() => {
    if (!getToken()) { navigate('/login'); return; }
    // Guard: if face_attraction missing, face step was skipped — go back to start
    if (!localStorage.getItem('face_attraction') && localStorage.getItem('swipe_test_done') !== 'true') {
      navigate('/onboarding/swipe-faces', { replace: true });
      return;
    }
    api.get('/profile')
      .then(p => {
        const o = (p.orientation || '').toLowerCase();
        const g = (p.gender || '').toLowerCase();
        if (o === 'men') setPhotos(maleBodyPhotos);
        else if (o === 'everyone') setPhotos(mixedBodyPhotos);
        else if (o === 'straight') setPhotos(['woman', 'women', 'female'].includes(g) ? maleBodyPhotos : femaleBodyPhotos);
        else setPhotos(femaleBodyPhotos);
      })
      .catch(() => setPhotos(femaleBodyPhotos));
  }, [navigate]);

  const save = async (payload) => {
    setSaving(true);
    setSaveError(false);
    try {
      await api.post('/profile', payload);
      localStorage.setItem('swipe_test_done', 'true');
      localStorage.removeItem('face_attraction');
      navigate('/onboarding/swipe-done');
    } catch {
      // Retry once after 2s
      try {
        await sleep(2000);
        await api.post('/profile', payload);
        localStorage.setItem('swipe_test_done', 'true');
        localStorage.removeItem('face_attraction');
        navigate('/onboarding/swipe-done');
      } catch {
        setSaving(false);
        setSaveError(true);
        setPendingPayload(payload);
      }
    }
  };

  const handleComplete = (photos, results) => {
    const body = scoreSwipe(photos, results);
    const face = JSON.parse(localStorage.getItem('face_attraction') || '{}');
    save({
      face_attraction_vata:     face.vata    ?? 33,
      face_attraction_pitta:    face.pitta   ?? 33,
      face_attraction_kapha:    face.kapha   ?? 34,
      face_attraction_dominant: face.dominant ?? 'vata',
      body_attraction_vata:     body.vata,
      body_attraction_pitta:    body.pitta,
      body_attraction_kapha:    body.kapha,
      body_attraction_dominant: body.dominant,
      swipe_test_completed:     true,
    });
  };

  if (saving) {
    return (
      <div style={{ height: '100vh', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span className="spinner" />
      </div>
    );
  }

  if (saveError) {
    return (
      <div style={{ height: '100vh', background: '#000', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 24, padding: '0 40px', textAlign: 'center' }}>
        <div style={{ fontSize: 13, color: 'var(--muted)' }}>Couldn't save your results. Check your connection.</div>
        <button
          onClick={() => save(pendingPayload)}
          style={{ padding: '14px 32px', borderRadius: 8, border: 'none', background: '#c9a84c', color: '#000', fontSize: 13, cursor: 'pointer' }}
        >
          Try again
        </button>
      </div>
    );
  }

  if (!photos) return <div style={{ height: '100vh', background: '#000' }} />;

  return (
    <SwipeTest
      photos={photos}
      label="BODY TYPE"
      prompt="Swipe right if this body type attracts you"
      onComplete={handleComplete}
    />
  );
}
